/**
 * Per-game catalog conventions: what each game's catalog means by the fields we read (TRO-492).
 *
 * The client mirror of `backend/src/trovie/domain/tcg/convention.py`. Its one rule is executed
 * against `shared/fixtures/printing-treatment.json` by both suites (via `sameCard`), so a game
 * added on one side and not the other fails the other side's suite.
 *
 * Every catalog read here is one provider's table, but the meaning of a field inside it is set by
 * the game, not the provider. The same `name` column does not mean the same thing in each:
 *
 *   Cloud, Midgar Mercenary (Borderless)    Magic    — an art treatment of "Cloud, Midgar Mercenary"
 *   Megavolt - Electrical Menace (Epic)     Lorcana  — same shape, same meaning
 *   Sol Ring                                Magic    — a name that identifies exactly one card
 *   Pikachu                                 Pokémon  — a name 100 sets print, identifying nothing
 *
 * No string rule separates those; only the game does, so the game-scoped answers live here in one
 * table rather than as another set beside each rule that needs one.
 *
 * Adding a game: an omitted game takes `DEFAULT_CONVENTION`, every rule's conservative setting and
 * the behaviour each caller already had. A game wrongly absent keeps the current output; a game
 * wrongly present changes it, so absence is the default.
 */

/** What one game's catalog does with the fields Trovie has to normalize. */
export type CatalogConvention = {
  /** Whether a trailing `(...)` on a product name is an art or edition treatment of the card named
   *  before it, rather than part of the card's own name. True for games that ship each treatment as
   *  its own product (own collector number, own price) and record which one only in the name. A
   *  claim about the shape of the name, never a list of accepted suffixes: the vocabulary is
   *  invented per set (Final Fantasy shipped a "Chocobo Track Foil"), while the shape has held for
   *  years. */
  treatmentInName: boolean;
};

/**
 * The server's table carries a second rule, `name_identifies_card`, which is deliberately not
 * mirrored here: only a surface that issues a catalog query needs it, and this client issues none;
 * it groups candidates the server already returned. A row in both tables that no shared fixture
 * case could exercise could drift apart silently, which "One rule, two languages" exists to prevent
 * (root `AGENTS.md`).
 */
export const DEFAULT_CONVENTION: CatalogConvention = { treatmentInName: false };

const CONVENTIONS: Record<string, CatalogConvention> = {
  magic: { treatmentInName: true },
  riftbound: { treatmentInName: true },
  // Pokémon's trailing parentheticals are treatments (TRO-296): Cosmos Holo, Poke Ball Pattern,
  // Master Ball Pattern, Prerelease, Staff, Pokemon Center Exclusive, "Destined Rivals Stamp". A
  // name like "Mr. Mime (Team Rocket's)" is not a counter-example, because TCGplayer writes that
  // card "Sabrina's Mr. Mime", with the trainer in front.
  //
  // Its treatments also split cleanly by collector number, along exactly the line image
  // recognition can see: in-set treatments that keep the base printing's number are the stamps and
  // holo patterns (a few millimetres of foil, which no recognizer resolves), while those that take
  // their own number are Full Art, Secret and Shiny, different artwork a recognizer separates
  // unaided.
  pokemon: { treatmentInName: true },
  // Japanese Pokémon is a separate TCGplayer category with its own id space, so it was checked on
  // its own rather than through its parent: its trailing parentheticals are the same class of
  // vocabulary (Mirror Holofoil, the ball patterns, Energy Symbol Pattern, and its own Pikachu,
  // Tyranitar and Cinderace stamps). Listed explicitly because a sub-game does not inherit its
  // parent's row.
  "pokemon-jp": { treatmentInName: true },
  // Lorcana's suffixes name a rarity or finish tier rather than an art direction ((Epic),
  // (Enchanted), (Iconic), (Foil), (Oversized)), but the convention is the one Magic established:
  // the treatment lives in the name, and each is a separate product at its own price. Reading them
  // as unrelated cards misleads an operator, as with the two Megavolts in Attack of the Vine! (#186
  // Uncommon at $0.08, #225 Epic at $1.77) (TRO-492).
  lorcana: { treatmentInName: true },
  // Fusion World names each printing by a trailing parenthetical on its own product, keeping the
  // base number: "Hercule - FB10-021" and "Hercule - FB10-021 (Alternate Art)", (Gold), promos.
  "dragonball-fusion-world": { treatmentInName: true },
};

/**
 * The conventions of `game`'s catalog, or the conservative default for one not listed.
 *
 * Matches on the canonical GAME_KEY case-insensitively and does not fold display labels the way
 * the server's `normalize_game` does: every `game` reaching this client comes from a candidate or
 * catalog row the API already stamped with a canonical key, so a label-folding pass here would be
 * dead code.
 */
export function catalogConvention(game: string | null | undefined): CatalogConvention {
  // `toLowerCase`, never `toLocaleLowerCase`: on a Turkish device the latter maps `I` to the
  // dotless `ı`, so a game arriving as "RIFTBOUND" would look up `rıftbound`, miss the table, and
  // silently take the conservative default on those phones alone (TRO-296).
  return CONVENTIONS[(game ?? "").trim().toLowerCase()] ?? DEFAULT_CONVENTION;
}
